import { boolean, object, string } from "yup";

import {
  BILLING_DAY_PATTERN,
  BILLING_DAY_RANGE_MESSAGE,
  SEQUENCE_ONLY_ONE_MESSAGE,
  SEQUENCE_REQUIRED_MESSAGE,
  SEQUENCE_TOKEN_REGEX,
} from "@/features/organization/invoice/constants/invoiceEnums";

// Message helpers mirror the repo convention (constants/invalids.ts) with the label embedded.
const requiredSelect = (label: string) => `「${label}」を選択してください。`;
const requiredInput = (label: string) => `「${label}」を入力してください。`;

/**
 * 請求日 / 支払締切（日）: 1〜29, required only when 末日に設定 is off (spec No.2 / No.5).
 * When 末日 is on the day is auto-resolved, so the field is optional.
 */
const billingDaySchema = (lastDayField: string, label: string) =>
  string()
    .label(label)
    .when(lastDayField, {
      is: false,
      then: (s) =>
        s
          .required(requiredInput(label))
          .matches(BILLING_DAY_PATTERN, { message: BILLING_DAY_RANGE_MESSAGE, excludeEmptyString: true }),
    });

/**
 * 請求設定 schema. Required fields follow the I/O spec (No.1–18); the two special rules:
 *   - 請求日 / 支払締切（日） 1〜29 & required when 末日 off (No.2 / No.5)
 *   - 請求書番号 must contain exactly one 連番 (No.11)
 * 請求書番号 format and the toggles/checkboxes are not required.
 */
export const invoiceSchema = object({
  billingMonth: string().label("請求月").required(requiredSelect("請求月")),
  billingDay: billingDaySchema("billingDayLastDay", "請求日"),
  billingDayLastDay: boolean().label("末日に設定"),
  paymentCutoffMonth: string().label("支払締切（月）").required(requiredSelect("支払締切（月）")),
  paymentCutoffDay: billingDaySchema("paymentCutoffLastDay", "支払締切（日）"),
  paymentCutoffLastDay: boolean().label("末日に設定"),
  joinMonthTreatment: string().label("入社月").required(requiredSelect("入社月")),
  leaveMonthTreatment: string().label("退職月").required(requiredSelect("退職月")),
  temporaryReturnTreatment: string().label("一時帰国").required(requiredSelect("一時帰国")),
  leaveOfAbsenceTreatment: string().label("休職").required(requiredSelect("休職")),
  invoiceNumberFormat: string()
    .label("請求書番号")
    .test({
      name: "sequence-rule",
      test: (value, ctx) => {
        const count = (value?.match(SEQUENCE_TOKEN_REGEX) ?? []).length;
        if (count === 0) return ctx.createError({ message: SEQUENCE_REQUIRED_MESSAGE });
        if (count > 1) return ctx.createError({ message: SEQUENCE_ONLY_ONE_MESSAGE });
        return true;
      },
    }),
  templateId: string().label("テンプレート"),
  defaultAssignee: string().label("既定担当者").required(requiredSelect("既定担当者")),
});
